import { Component, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SubmenuStateService } from './submenu-state.service';
import { SidebarLockService } from './sidebar-lock.service';
import { SubmenuItem, SubmenuSection, TopMenuEntry } from './submenu-model';
import { hasAnyRole } from '../role.util';
import { PermissionStore } from '@/pages/service/core/permission-store';
import { SeoStatusService } from '@/pages/seo/seo-status.service';
import { VerticalConfigService } from '@/pages/service/core/vertical-config.service';

@Component({
    selector: 'app-submenu-panel',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './app.submenu-panel.html',
    styleUrls: ['./app.submenu-panel.scss']
})
export class AppSubmenuPanel implements OnInit, OnDestroy {

    @HostBinding('class.is-open') get isOpen(): boolean { return !!this.entry; }

    private state = inject(SubmenuStateService);
    private router = inject(Router);
    private lockService = inject(SidebarLockService);
    private perms = inject(PermissionStore);
    private seoStatus = inject(SeoStatusService);
    private verticalConfig = inject(VerticalConfigService);

    entry: TopMenuEntry | null = null;
    private activeRoute = '';
    private activeQueryParams: Record<string, string> = {};
    private geoEnabled: boolean | null = null;   // gates 'geo' add-on items (AI Visibility)
    private subs = new Subscription();

    ngOnInit(): void {
        this.activeRoute = this.cleanUrl(this.router.url);
        this.activeQueryParams = this.parseQuery(this.router.url);

        this.subs.add(this.state.activeEntry().subscribe(e => this.entry = e));
        this.subs.add(this.seoStatus.geoEnabled$.subscribe(v => this.geoEnabled = v));
        this.subs.add(
            this.router.events
                .pipe(filter(e => e instanceof NavigationEnd))
                .subscribe((e: any) => {
                    const url = (e as NavigationEnd).urlAfterRedirects;
                    this.activeRoute = this.cleanUrl(url);
                    this.activeQueryParams = this.parseQuery(url);
                })
        );
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    visibleSections(): SubmenuSection[] {
        if (!this.entry?.sections) return [];
        return this.entry.sections
            .map(section => ({
                ...section,
                items: section.items.filter(i => (!i.roles || hasAnyRole(...i.roles))
                    && (i.addon !== 'geo' || this.geoEnabled === true)   // hide AI Visibility until GEO is enabled
                    && !(i.proHidden === true && this.verticalConfig.proMode())   // hide journeys/users/teams in Pro mode
                    && !(i.proOnly === true && !this.verticalConfig.proMode())    // Pro-only items (e.g. Statuses) hidden outside Pro
                    && this.perms.menuAllowed(this.itemUrl(i)))   // strict: hide unmapped/ungranted sub-items for restricted users
            }))
            .filter(section => section.items.length > 0);
    }

    /** Full URL (path + ?query) for an item, used to look up its screen permission. */
    private itemUrl(item: SubmenuItem): string {
        const path = '/' + item.routerLink.join('/').replace(/^\/+/, '');
        if (item.queryParams) {
            const qp = new URLSearchParams(item.queryParams as any).toString();
            return qp ? path + '?' + qp : path;
        }
        return path;
    }

    isActive(item: SubmenuItem): boolean {
        const itemPath = this.keyOf(item);
        const pathMatches = this.activeRoute === itemPath || this.activeRoute.startsWith(itemPath + '/');
        if (!pathMatches) return false;

        if (item.queryParams) {
            for (const [key, value] of Object.entries(item.queryParams)) {
                if (this.activeQueryParams[key] !== value) return false;
            }
        }
        return true;
    }

    /** Returns the key (router path) of the item that best matches the current URL. */
    private get bestMatchKey(): string | null {
        if (!this.entry?.sections) return null;
        let best: string | null = null;
        let bestLen = -1;
        for (const section of this.entry.sections) {
            for (const item of section.items) {
                const path = this.keyOf(item);
                if (this.activeRoute === path || this.activeRoute.startsWith(path + '/')) {
                    if (path.length > bestLen) {
                        best = path;
                        bestLen = path.length;
                    }
                }
            }
        }
        return best;
    }

    private keyOf(item: SubmenuItem): string {
        return '/' + item.routerLink.join('/').replace(/^\/+/, '');
    }

    private cleanUrl(url: string): string {
        return url.split('?')[0].split('#')[0];
    }

    private parseQuery(url: string): Record<string, string> {
        const i = url.indexOf('?');
        if (i < 0) return {};
        const out: Record<string, string> = {};
        new URLSearchParams(url.substring(i + 1)).forEach((v, k) => { out[k] = v; });
        return out;
    }

    close(): void {
        this.state.close();
    }

    onMouseLeave(event: MouseEvent): void {
        // Leaving the panel — release the sidebar lock unless the pointer is
        // moving back into the sidebar itself.
        const target = event.relatedTarget as Element | null;
        if (target && target.closest && target.closest('app-sidebar')) {
            return;
        }
        this.lockService.setLocked(false);
    }
}
